// React error boundary specialised for the "lazy chunk vanished after
// deploy" case. When a new build ships, hashed asset filenames change
// AND the previous build's chunks are removed from S3 by the --delete
// sync + CloudFront invalidation. A long-lived tab still holding the
// old index.html will hit a 404 the first time it tries to lazy-import
// a route. The error name is "ChunkLoadError" in webpack-flavoured
// bundlers and a plain Error with a known message in Vite/Rollup. We
// match on both shapes, reload once, and surface a minimal "something
// went wrong" page if the reload itself failed to recover (e.g. truly
// broken deploy).
import { Component, type ReactNode } from 'react';
import i18n from '../i18n/i18n';

interface Props { children: ReactNode }
interface State { hasError: boolean }

const RELOAD_GUARD_KEY = 'badges.chunkReloadAttempted';

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: string; message?: string };
  if (e.name === 'ChunkLoadError') return true;
  const msg = String(e.message ?? '');
  return (
    msg.includes('Failed to fetch dynamically imported module')
    || msg.includes('Loading chunk')
    || msg.includes('Importing a module script failed')
    || msg.includes('error loading dynamically imported module')
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) {
      // Not our case — let React surface it normally. Caller can wrap
      // a more general error boundary above us if they want.
      return;
    }
    // Reload exactly once per session to avoid an infinite loop if
    // version.json or the new bundle are still genuinely broken.
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === '1') return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
          <div className="max-w-sm rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
            <h2 className="text-base font-semibold text-gray-900">{i18n.t('common:chunkError.title', { defaultValue: 'Something went wrong loading the app.' })}</h2>
            <p className="mt-2 text-sm text-gray-600">
              {i18n.t('common:chunkError.body', { defaultValue: 'Try a hard refresh — if it keeps happening, contact support.' })}
            </p>
            <button
              type="button"
              onClick={() => {
                sessionStorage.removeItem(RELOAD_GUARD_KEY);
                window.location.reload();
              }}
              className="mt-4 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              {i18n.t('common:chunkError.reload', { defaultValue: 'Reload' })}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
